/**
 * Iter 152q — Filet anti-typo pour les noms / prénoms du formulaire public.
 *
 * Cas réel observé (BENSASSI) :
 *   - Voy 1 saisi : "BENSASI"  (clavier prédictif Android — S manquant)
 *   - Voy 2 saisi : "BENSASS"  (autre suggestion clavier — I manquant)
 *   - Email du foyer : "bensassi.sonialyon@..." (orthographe correcte)
 *
 * On retourne une liste de WARNINGS (strings) que l'appelant utilise dans un
 * window.confirm() non bloquant à la soumission du formulaire.
 *
 * Règles (volontairement conservatrices pour éviter le faux positif) :
 *   1. nom voyageur vs partie locale de l'email du foyer :
 *      - on ignore si email vide ou pas de "@"
 *      - on extrait des "mots" alpha (≥ 4 lettres) de la partie locale
 *      - distance Levenshtein 1 ou 2 entre un mot et un nom voyageur → warning
 *      - distance 0 (parfait) → pas de warning
 *   2. noms entre voyageurs du même foyer :
 *      - on compare 2 à 2 les noms ≥ 5 lettres
 *      - distance 1 ou 2 → warning ("est-ce le même nom ?")
 *      - distance 0 (homonymes parfaits) → pas de warning (peut être 2 frères)
 */

function normalize(s) {
  return (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")  // retire les accents
    .toLowerCase()
    .replace(/[^a-z]/g, "");           // garde uniquement a-z
}

/**
 * Distance de Levenshtein entre deux chaînes ASCII normalisées.
 * Renvoie le nombre minimal d'opérations (insertion/suppression/substitution).
 */
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const al = a.length, bl = b.length;
  // matrice [al+1][bl+1] linéarisée
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,        // insertion
        prev[j] + 1,            // suppression
        prev[j - 1] + cost,     // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

function emailLocalPart(email) {
  if (!email || !email.includes("@")) return "";
  return email.split("@")[0];
}

/**
 * Extrait les "mots" candidats de la partie locale d'un email.
 * Sépare sur '.', '_', '-', chiffres → garde ceux ≥ 4 lettres.
 */
function extractEmailNameTokens(email) {
  const local = emailLocalPart(email);
  if (!local) return [];
  return local
    .split(/[\.\-_0-9]+/)
    .map(normalize)
    .filter(tok => tok.length >= 4);
}

/**
 * Retourne la liste des messages de warning à afficher.
 * Chaque message est rédigé en français à destination du voyageur.
 */
export function detectTypoWarnings(voyageurs, email) {
  const warnings = [];
  const seenPairs = new Set();  // évite les doublons (A vs B identique à B vs A)

  const remplis = (voyageurs || [])
    .map((v, idx) => ({
      idx,
      nom: (v && v.nom) || "",
      prenom: (v && v.prenom) || "",
      nomNorm: normalize(v && v.nom),
      prenomNorm: normalize(v && v.prenom),
    }))
    .filter(v => v.nomNorm);  // ne considère que les voyageurs avec un nom

  // --- (1) Nom voyageur vs email du foyer ---
  const tokens = extractEmailNameTokens(email);
  for (const v of remplis) {
    if (v.nomNorm.length < 4) continue;  // trop court → trop de faux positifs
    let bestDistance = Infinity;
    let bestToken = "";
    let hasExact = false;
    for (const tok of tokens) {
      const d = levenshtein(v.nomNorm, tok);
      if (d === 0) {
        hasExact = true;
        break;
      }
      if (d < bestDistance) {
        bestDistance = d;
        bestToken = tok;
      }
    }
    // Warning UNIQUEMENT si proche (distance 1-2) ET aucun match exact dans l'email
    if (!hasExact && bestDistance >= 1 && bestDistance <= 2 && bestToken) {
      warnings.push(
        `• L'email contient « ${bestToken.toUpperCase()} », mais tu as saisi ` +
        `« ${v.nom.toUpperCase()} » pour le voyageur ${v.idx + 1}. ` +
        `Est-ce bien la bonne orthographe ?`
      );
    }
  }

  // --- (2) Noms proches entre voyageurs du même foyer ---
  for (let i = 0; i < remplis.length; i++) {
    for (let j = i + 1; j < remplis.length; j++) {
      const a = remplis[i], b = remplis[j];
      if (a.nomNorm.length < 5 || b.nomNorm.length < 5) continue;
      if (a.nomNorm === b.nomNorm) continue;  // homonymes parfaits = OK (frères)
      const d = levenshtein(a.nomNorm, b.nomNorm);
      if (d < 1 || d > 2) continue;
      const key = [Math.min(a.idx, b.idx), Math.max(a.idx, b.idx)].join("-");
      if (seenPairs.has(key)) continue;
      seenPairs.add(key);
      warnings.push(
        `• Les noms « ${a.nom.toUpperCase()} » (voyageur ${a.idx + 1}) et ` +
        `« ${b.nom.toUpperCase()} » (voyageur ${b.idx + 1}) sont presque identiques. ` +
        `Si c'est le même nom de famille, merci d'aligner l'orthographe.`
      );
    }
  }

  return warnings;
}
